// Rounded line paths for the charts.
//
// The obvious way to round a line is Catmull-Rom, and it looks lovely — right
// up to the point where it invents a peak. Between a low value and a high one
// it overshoots past both, so a chart reporting "0 calls on Tuesday" draws a
// curve dipping BELOW zero, and a flat run before a spike grows a bump that is
// not in the data. A chart may not draw numbers nobody measured.
//
// So this is monotone cubic interpolation (Fritsch–Carlson): smooth everywhere,
// and never above the higher of two neighbouring points nor below the lower
// one. Where the data is flat, the curve is flat.

export type Pt = readonly [number, number];

/**
 * Tangents that keep every segment monotone — the trick is a flat tangent
 * wherever the data turns, and clamping to three times the smaller neighbouring
 * slope everywhere else.
 */
function tangents(pts: readonly Pt[]): number[] {
  const n = pts.length;
  const slope: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const h = pts[i + 1]![0] - pts[i]![0];
    slope.push(h === 0 ? 0 : (pts[i + 1]![1] - pts[i]![1]) / h);
  }

  const m: number[] = new Array<number>(n).fill(0);
  m[0] = slope[0] ?? 0;
  m[n - 1] = slope[n - 2] ?? 0;
  for (let i = 1; i < n - 1; i++) {
    const a = slope[i - 1]!;
    const b = slope[i]!;
    // A local peak or trough: flat tangent, so the curve turns AT the point
    // instead of sailing past it.
    m[i] = a * b <= 0 ? 0 : (a + b) / 2;
  }
  for (let i = 0; i < n - 1; i++) {
    const s = slope[i]!;
    if (s === 0) {
      m[i] = 0;
      m[i + 1] = 0;
      continue;
    }
    const a = m[i]! / s;
    const b = m[i + 1]! / s;
    const h = Math.hypot(a, b);
    if (h > 3) {
      m[i] = (3 / h) * a * s;
      m[i + 1] = (3 / h) * b * s;
    }
  }
  return m;
}

/** An SVG path (`M … C …`) through the points: rounded, never overshooting. */
export function smoothPath(pts: readonly Pt[]): string {
  if (pts.length === 0) return '';
  const fmt = (v: number) => v.toFixed(1);
  if (pts.length === 1) return `M${fmt(pts[0]![0])},${fmt(pts[0]![1])}`;
  if (pts.length === 2) {
    return `M${fmt(pts[0]![0])},${fmt(pts[0]![1])} L${fmt(pts[1]![0])},${fmt(pts[1]![1])}`;
  }

  const m = tangents(pts);
  let d = `M${fmt(pts[0]![0])},${fmt(pts[0]![1])}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[i]!;
    const [x1, y1] = pts[i + 1]!;
    const h = (x1 - x0) / 3;
    d += ` C${fmt(x0 + h)},${fmt(y0 + m[i]! * h)} ${fmt(x1 - h)},${fmt(y1 - m[i + 1]! * h)} ${fmt(x1)},${fmt(y1)}`;
  }
  return d;
}
